import React from 'react';
import AccountantSidebar from './components/AccountantSidebar';
import Header from '../teacher_screen/components/Header';
import { useLanguage } from '../../../context/LanguageContext';
import {
    DollarSign,
    ArrowUpRight,
    TrendingUp,
    Clock,
    CheckCircle,
    AlertCircle,
    MoreHorizontal,
    BarChart3,
    ArrowDownRight,
    Search
} from 'lucide-react';

export default function AccountantDashboard() {
    const { t } = useLanguage();
    const stats = [
        { label: 'Total Collections', value: '₹12.4L', trend: '+8.2% vs last month', icon: CheckCircle, color: 'text-emerald-500 bg-emerald-50' },
        { label: 'Pending Fees', value: '₹2.8L', trend: '15 students overdue', icon: AlertCircle, color: 'text-amber-500 bg-amber-50' },
        { label: 'Total Revenue', value: '₹48.2L', trend: 'Projected: ₹55L', icon: TrendingUp, color: 'text-blue-500 bg-blue-50' },
        { label: 'Active Discounts', value: '18', trend: 'Scholarship cases', icon: DollarSign, color: 'text-rose-500 bg-rose-50' },
    ];

    const recentTransactions = [
        { id: 1, student: 'Rohit Sharma', date: 'Mar 09, 2026', amount: '₹12,000', method: 'UPI', status: 'Paid', class: 'Grade 10-A' },
        { id: 2, student: 'Sanya Mirza', date: 'Mar 08, 2026', amount: '₹8,500', method: 'Cash', status: 'Paid', class: 'Grade 8-B' },
        { id: 3, student: 'Virat Kohli', date: 'Mar 08, 2026', amount: '₹15,000', method: 'Bank Transfer', status: 'Pending', class: 'Grade 11-A' },
        { id: 4, student: 'PV Sindhu', date: 'Mar 07, 2026', amount: '₹10,500', method: 'Card', status: 'Paid', class: 'Grade 9-C' },
    ];

    return (
        <div className="flex h-screen bg-[#F8FAFC] overflow-hidden font-sans">
            <AccountantSidebar />

            <main className="flex-1 overflow-y-auto px-8">
                <Header
                    title={t('financeConsole')}
                    subtitle={t('financeConsoleSub')}
                >
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-xl text-[14px] font-black text-blue-600 shadow-sm cursor-pointer hover:bg-blue-100 transition-all">
                            ACADEMIC YEAR 2025-26
                        </div>
                    </div>
                </Header>

                <div className="max-w-7xl mx-auto">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                        {stats.map((stat, idx) => (
                            <div key={idx} className="bg-white p-6 rounded-[24px] border border-gray-100 shadow-sm hover:scale-[1.02] transition-all cursor-default group">
                                <div className="flex items-center justify-between mb-4">
                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${stat.color} group-hover:shadow-lg transition-all`}>
                                        <stat.icon size={22} strokeWidth={2.5} />
                                    </div>
                                    <div className="text-right">
                                        <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest leading-none">{stat.label}</span>
                                        <h4 className="text-[28px] font-black text-[#111827] mt-1 tracking-tight">{stat.value}</h4>
                                    </div>
                                </div>
                                <div className={`flex items-center gap-1.5 text-[12px] font-bold ${stat.label === 'Pending Fees' ? 'text-amber-500' : 'text-emerald-500'} opacity-80`}>
                                    {stat.label === 'Pending Fees' ? <AlertCircle size={14} /> : <TrendingUp size={14} />}
                                    {stat.trend}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10 pb-10">
                        {/* Transaction Table */}
                        <div className="lg:col-span-2 bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                            <div className="p-8 border-b border-gray-50 flex items-center justify-between">
                                <div>
                                    <h3 className="text-[20px] font-black text-[#111827] uppercase tracking-tighter">{t('recentCollections')}</h3>
                                    <p className="text-gray-400 text-[13px] font-bold uppercase tracking-widest mt-1">Daily fee inflow tracking</p>
                                </div>
                                <button className="text-blue-500 font-black text-[13px] uppercase tracking-widest hover:underline">{t('viewAll')}</button>
                            </div>
                            <div className="overflow-x-auto flex-1">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="bg-gray-50/50">
                                            <th className="px-8 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest">Student</th>
                                            <th className="px-8 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest">Amount</th>
                                            <th className="px-8 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest">Method</th>
                                            <th className="px-8 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest text-right">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {recentTransactions.map((tx) => (
                                            <tr key={tx.id} className="hover:bg-blue-50/20 transition-colors group">
                                                <td className="px-8 py-6">
                                                    <div>
                                                        <span className="block text-[15px] font-black text-[#111827] uppercase tracking-tight leading-none mb-1.5">{tx.student}</span>
                                                        <span className="text-[12px] font-bold text-gray-400 uppercase">{tx.class}</span>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-6 text-[15px] font-black text-[#111827] tracking-tight">{tx.amount}</td>
                                                <td className="px-8 py-6">
                                                    <span className="text-[13px] font-bold text-gray-600">{tx.method}</span>
                                                </td>
                                                <td className="px-8 py-6 text-right">
                                                    <span className={`px-4 py-1.5 rounded-xl text-[11px] font-black uppercase tracking-widest ${tx.status === 'Paid' ? 'text-emerald-500 bg-emerald-50' : 'text-amber-500 bg-amber-50'
                                                        }`}>
                                                        {tx.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Summary Card */}
                        <div className="space-y-6">
                            <div className="bg-[#111827] p-8 rounded-[32px] shadow-2xl text-white relative flex flex-col justify-between overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500 opacity-20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                                <div className="relative z-10">
                                    <h3 className="text-[12px] font-black uppercase tracking-[0.3em] text-blue-400 mb-6">{t('totalLiquidity')}</h3>
                                    <div className="flex items-baseline gap-2 mb-2">
                                        <span className="text-[36px] font-black tracking-tighter">₹8.4L</span>
                                        <span className="text-gray-400 text-[13px] font-bold uppercase tracking-widest">Available</span>
                                    </div>
                                    <p className="text-gray-400 text-[14px] leading-relaxed mb-8">All financial parameters are currently within institutional safety margins.</p>
                                    <button className="w-full py-4 bg-blue-500 text-white rounded-2xl text-[14px] font-black uppercase tracking-widest hover:bg-opacity-90 transition-all shadow-xl shadow-blue-900/20">
                                        {t('initiateSettlement')}
                                    </button>
                                </div>
                            </div>

                            <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm overflow-hidden">
                                <div className="flex items-center justify-between mb-8">
                                    <div className="w-12 h-12 bg-gray-50 text-blue-500 rounded-2xl flex items-center justify-center">
                                        <BarChart3 size={24} />
                                    </div>
                                    <div className="text-right">
                                        <h4 className="text-[12px] font-black text-gray-400 uppercase tracking-widest">{t('collectionRate')}</h4>
                                        <p className="text-[22px] font-black text-[#111827]">92.4%</p>
                                    </div>
                                </div>
                                <div className="h-2 w-full bg-gray-50 rounded-full overflow-hidden mb-4">
                                    <div className="h-full bg-blue-500 rounded-full w-[92.4%]"></div>
                                </div>
                                <p className="text-[12px] font-bold text-gray-400 text-center uppercase tracking-widest">Target for Mar: 98.0%</p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
